import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Users,
  Settings,
  Wallet,
  Zap,
  ArrowLeftRight,
  FileText,
  Bell,
  User,
  LogOut,
  ChevronLeft,
  Grid3X3,
  Search,
  Plus,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  color: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { id: 'pix', label: 'PIX', icon: Zap, path: '/banking/pix', color: 'from-green-500 to-emerald-600' },
  { id: 'transfer', label: 'Transferir', icon: ArrowLeftRight, path: '/banking/transfers', color: 'from-blue-500 to-cyan-600' },
  { id: 'accounts', label: 'Contas', icon: Wallet, path: '/banking/accounts', color: 'from-purple-500 to-violet-600' },
  { id: 'statements', label: 'Extrato', icon: FileText, path: '/banking/statements', color: 'from-orange-500 to-amber-600' },
];

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Início', icon: Home, path: '/banking/dashboard' },
  { id: 'accounts', label: 'Contas', icon: Wallet, path: '/banking/accounts' },
  { id: 'pix', label: 'PIX', icon: Zap, path: '/banking/pix' },
  { id: 'transfers', label: 'Transferências', icon: ArrowLeftRight, path: '/banking/transfers' },
  { id: 'statements', label: 'Extrato', icon: FileText, path: '/banking/statements' },
];

function QuickActionCard({ action }: { action: QuickAction }) {
  return (
    <Link
      to={action.path}
      className={cn(
        'flex flex-col items-center justify-center p-4 rounded-xl',
        'bg-gradient-to-br',
        action.color,
        'hover:scale-105 transition-transform shadow-lg'
      )}
    >
      <action.icon className="w-6 h-6 text-white mb-2" />
      <span className="text-white text-sm font-medium">{action.label}</span>
    </Link>
  );
}

function BottomNav() {
  const location = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-16 bg-surface-900/95 backdrop-blur-xl border-t border-surface-800 z-40 md:hidden">
      <div className="h-full flex items-center justify-around px-2">
        {NAV_ITEMS.slice(0, 5).map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                'flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors',
                isActive
                  ? 'text-primary-400'
                  : 'text-surface-400 hover:text-white'
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function DashboardFirstLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);

  const isDashboard = location.pathname === '/banking/dashboard' || location.pathname === '/';
  const canGoBack = !isDashboard;

  return (
    <div className="min-h-screen bg-surface-950 pb-16 md:pb-0">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-30" />
      </div>

      {/* Header */}
      <header className="sticky top-0 bg-surface-900/95 backdrop-blur-xl border-b border-surface-800 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left Side */}
            <div className="flex items-center gap-3">
              {canGoBack ? (
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <span className="text-white font-bold">U</span>
                </div>
              )}
              
              {isDashboard ? (
                <div>
                  <div className="text-surface-400 text-xs">Olá,</div>
                  <div className="text-white font-semibold">{user?.name || 'Usuário'}</div>
                </div>
              ) : (
                <h1 className="text-white font-semibold">
                  {NAV_ITEMS.find(i => location.pathname.includes(i.path))?.label || 'Página'}
                </h1>
              )}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
              <button className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              <Link
                to="/profile"
                className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center"
              >
                <User className="w-5 h-5 text-primary-400" />
              </Link>
            </div>
          </div>

          {/* Search Bar */}
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3">
                  <div className="flex items-center gap-2 px-4 py-2 bg-surface-800 rounded-lg">
                    <Search className="w-4 h-4 text-surface-400" />
                    <input
                      type="text"
                      placeholder="Buscar..."
                      className="flex-1 bg-transparent text-white text-sm focus:outline-none"
                      autoFocus
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick Actions (only on dashboard) */}
        {isDashboard && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-4 gap-3">
              {QUICK_ACTIONS.map((action) => (
                <QuickActionCard key={action.id} action={action} />
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-200px)]">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="p-4"
        >
          <Outlet />
        </motion.div>
      </main>

      {/* Desktop Sidebar (hidden on mobile) */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-20 bg-surface-900/95 backdrop-blur-xl border-r border-surface-800 flex-col items-center py-4 z-30">
        {/* Logo */}
        <Link
          to="/banking/dashboard"
          className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6"
        >
          <span className="text-white font-bold text-lg">U</span>
        </Link>

        {/* Nav Items */}
        <nav className="flex-1 flex flex-col items-center gap-2">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center transition-colors',
                  isActive
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'text-surface-400 hover:bg-surface-800 hover:text-white'
                )}
                title={item.label}
              >
                <item.icon className="w-5 h-5" />
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="flex flex-col items-center gap-2">
          <Link
            to="/settings"
            className="w-12 h-12 rounded-xl flex items-center justify-center text-surface-400 hover:bg-surface-800 hover:text-white transition-colors"
            title="Configurações"
          >
            <Settings className="w-5 h-5" />
          </Link>
          <button
            onClick={() => logout()}
            className="w-12 h-12 rounded-xl flex items-center justify-center text-red-400 hover:bg-surface-800 transition-colors"
            title="Sair"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </aside>

      {/* Bottom Navigation (mobile only) */}
      <BottomNav />
    </div>
  );
}

export default DashboardFirstLayout;
